import type React from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Rendu markdown compact pour les réponses de l'assistant (text-sm, tables
 * scrollables, pas de dangerouslySetInnerHTML — react-markdown produit du JSX).
 */
const components: Components = {
  h1: ({ children }) => <h3 className="mt-4 mb-1.5 text-sm font-semibold first:mt-0">{children}</h3>,
  h2: ({ children }) => <h3 className="mt-4 mb-1.5 text-sm font-semibold first:mt-0">{children}</h3>,
  h3: ({ children }) => <h4 className="mt-3 mb-1 text-sm font-semibold first:mt-0">{children}</h4>,
  p: ({ children }) => <p className="my-1.5 leading-relaxed first:mt-0 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="my-1.5 list-disc space-y-0.5 pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="my-1.5 list-decimal space-y-0.5 pl-5">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer" className="link">
      {children}
    </a>
  ),
  hr: () => <hr className="my-3 border-gray-100" />,
  code: ({ children, className }) =>
    className ? (
      <code className="block overflow-x-auto rounded-md bg-gray-50 p-2 font-mono text-xs">{children}</code>
    ) : (
      <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-xs">{children}</code>
    ),
  pre: ({ children }) => <pre className="my-2">{children}</pre>,
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-gray-200 px-2 py-1.5 text-left font-semibold">{children}</th>
  ),
  td: ({ children }) => <td className="border-b border-gray-100 px-2 py-1.5">{children}</td>,
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-2 border-gray-200 pl-3 text-muted-foreground">{children}</blockquote>
  ),
};

export function ChatMarkdown({ children }: { children: string }): React.ReactElement {
  return (
    <div className="text-sm text-gray-900">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
